import { getColorForSelector } from './dom'
import {
  FabricDependencies,
  ICustomizations,
  IPalette,
  ITheme,
} from './fabric-dependencies'
import { IPageContext, SurfaceColors, SurfaceParams } from './models'

const getColor = (color?: string, fallback?: string) =>
  color
    ? color.startsWith('.')
      ? getColorForSelector(color) || fallback
      : color
    : fallback

const buildPalette = (
  {
    getColorFromString,
    getContrastRatio,
    DefaultPalette,
    ThemeGenerator,
    themeRulesStandardCreator,
  }: FabricDependencies,
  primaryColor: string,
  secondaryColor: string
): Partial<IPalette> => {
  const theme = themeRulesStandardCreator()

  ThemeGenerator.setSlot(theme.primaryColor, primaryColor, false, true)
  ThemeGenerator.setSlot(theme.themeTertiary, secondaryColor, false, true)

  const useWhite =
    getContrastRatio(
      getColorFromString(DefaultPalette.white)!,
      theme.themePrimary.color!
    ) >
    getContrastRatio(
      getColorFromString(DefaultPalette.black)!,
      theme.themePrimary.color!
    )

  ThemeGenerator.setSlot(
    theme.white,
    useWhite ? DefaultPalette.white : DefaultPalette.black,
    false,
    true
  )

  return ThemeGenerator.getThemeAsJson(theme) as Partial<IPalette>
}

/**
 * Given an existing customizer, enables overriding the set of colors used
 * by coachmark and teaching callouts
 *
 * @param customizer Existing customizer
 * @param overrides Set of colors to override
 */
export const overrideCustomizer = (
  deps: FabricDependencies,
  customizer: ICustomizations | undefined,
  overrides: SurfaceColors &
    Pick<IPageContext, 'primaryColour' | 'secondaryColour'> &
    Pick<SurfaceParams, 'disableHostTheme'>
): ICustomizations | undefined => {
  const { scopedSettings = {}, settings = {} } =
    customizer || ({} as ICustomizations)

  const primary =
    overrides.primaryColor ||
    (!overrides.disableHostTheme && overrides.primaryColour) ||
    overrides.backgroundColor ||
    deps.DefaultPalette.neutralLighter

  const secondary =
    overrides.secondaryColor ||
    (!overrides.disableHostTheme && overrides.secondaryColour) ||
    deps.DefaultPalette.neutralSecondary

  const initialPalette: Partial<IPalette> = buildPalette(
    deps,
    primary,
    secondary
  )

  const theme =
    (settings.theme as ITheme) ?? deps.createTheme({ palette: initialPalette })

  const palette = theme.palette

  return {
    ...customizer,
    scopedSettings,
    settings: {
      ...settings,
      theme: {
        ...theme,
        palette: {
          ...palette,
          themeDark: getColor(overrides.buttonHoverColor, palette.themeDark),
          themeDarkAlt: getColor(
            overrides.buttonHoverColor,
            palette.themeDarkAlt
          ),
          themeLighter: getColor(
            overrides.primaryButtonHoverColor,
            palette.themeLighter
          ),
          themePrimary: getColor(
            overrides.backgroundColor,
            palette.themePrimary
          ),
          themeTertiary: getColor(overrides.beaconColor, palette.themeTertiary),
          white: getColor(overrides.textColor, palette.white),
        },
      },
    },
  }
}
